import { useMutation } from '@tanstack/react-query'
import { App } from 'antd'
import { useTranslation } from 'react-i18next'

import {
  ordersUpdateFulfillmentOrderShipmentTrackingMutation,
  type CarrierRoot,
  type ChangeShipmentTrackingResponse,
} from '@repo/sdk'

import { extractUserFacingMessage } from '../../providers/sdkErrorUtils'

import { useInvalidateFulfillmentOrders } from './useInvalidateFulfillmentOrders'

interface ChangeTrackingParams {
  fulfillmentOrderId: string
  carrier: CarrierRoot
  trackingNumber: string
  onDone: () => void
}

export function useChangeTracking() {
  const invalidateFulfillmentOrders = useInvalidateFulfillmentOrders()
  const { message } = App.useApp()
  const { t } = useTranslation()

  const { mutate, isPending } = useMutation({
    ...ordersUpdateFulfillmentOrderShipmentTrackingMutation(),
    onSuccess: async (data: ChangeShipmentTrackingResponse, variables) => {
      const status = data.validationStatus
      if (status === 'VALID') message.success(t('pages.orders.changeTracking.success'))
      else message.warning(t(`pages.orders.trackingValidation.status.${status}`))
      // Refresh detail (new carrier/tracking number) and live tracking status together.
      await invalidateFulfillmentOrders(variables.path?.fulfillmentOrderId)
    },
    onError: (error) => {
      message.error(extractUserFacingMessage(error) ?? t('pages.orders.changeTracking.error'))
    },
  })

  const changeTracking = ({
    fulfillmentOrderId,
    carrier,
    trackingNumber,
    onDone,
  }: ChangeTrackingParams) => {
    mutate(
      {
        path: { fulfillmentOrderId },
        body: { carrier, trackingNumber },
      },
      { onSuccess: onDone },
    )
  }

  return { changeTracking, isChanging: isPending }
}
